import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_neon-narrative-hub/artifacts/qo1g5e0z_grok_video_2026-04-24-18-44-55.mp4";

const InfraPower = () => {
  const videoRef = useRef(null);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let timer;
    const onPlaying = () => {
      timer = setTimeout(() => setTextVisible(true), 400);
    };
    v.addEventListener("playing", onPlaying, { once: true });
    const fallback = setTimeout(() => setTextVisible(true), 1200);
    const p = v.play?.();
    if (p && typeof p.catch === "function") {
      p.catch(() => setTextVisible(true));
    }
    return () => {
      v.removeEventListener("playing", onPlaying);
      clearTimeout(timer);
      clearTimeout(fallback);
    };
  }, []);

  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      id="infra"
      className="relative w-full overflow-hidden snap-section snap-reveal"
      data-testid="section-infra"
    >
      <div className="relative w-full h-[70vh] min-h-[520px] md:h-[85vh] md:min-h-[640px]">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="infra-video"
        />

        {/* Dark readability overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,10,0.78) 0%, rgba(5,5,10,0.4) 20%, rgba(5,5,10,0.4) 60%, rgba(5,5,10,0.82) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 28%, rgba(5,5,10,0.58) 100%)",
          }}
        />

        {/* Edge fades */}
        <div
          className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(180deg, #05050A 0%, transparent 100%)" }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(0deg, #05050A 0%, transparent 100%)" }}
        />

        {/* Scanlines */}
        <div
          className="absolute inset-0 pointer-events-none opacity-25"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)",
            mixBlendMode: "overlay",
          }}
        />

        {/* Centered text */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          {/* Block 1 — INFRASTRUCTURE */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
            transition={{ duration: 1.1, ease }}
            className="font-display font-black uppercase tracking-tight leading-[1.0] text-white interactive-text interactive-text--cyan"
            style={{
              fontSize: "clamp(1.6rem, 7vw, 5.5rem)",
              color: "#7BD5FF",
              textShadow:
                "0 0 26px rgba(0,180,255,0.65), 0 0 56px rgba(0,240,255,0.35), 0 2px 22px rgba(0,0,0,0.75)",
              wordBreak: "break-word",
            }}
            data-testid="infra-line-1a"
          >
            Infrastructure
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ duration: 1.05, ease, delay: 0.25 }}
            className="mt-2 md:mt-3 font-display font-black uppercase tracking-tight leading-[1.0] text-white"
            style={{
              fontSize: "clamp(2rem, 7vw, 5.5rem)",
              textShadow: "0 2px 22px rgba(0,0,0,0.7)",
            }}
            data-testid="infra-line-1b"
          >
            Drives Power.
          </motion.div>

          {/* Accent bar */}
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={textVisible ? { opacity: 1, width: "120px" } : { opacity: 0, width: 0 }}
            transition={{ duration: 1.1, ease, delay: 0.55 }}
            className="h-[1.5px] my-8 md:my-10"
            style={{
              background:
                "linear-gradient(90deg, transparent, #00C8FF 40%, #FFB845 80%, transparent)",
              boxShadow: "0 0 10px rgba(0,200,255,0.55)",
            }}
          />

          {/* Block 2 — WE TRACK / BHARAT'S RISE */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ duration: 1.05, ease, delay: 0.8 }}
            className="font-display font-black uppercase tracking-tight leading-[1.04] text-white"
            style={{
              fontSize: "clamp(1.75rem, 5.2vw, 4.25rem)",
              textShadow: "0 2px 20px rgba(0,0,0,0.7)",
            }}
            data-testid="infra-line-2a"
          >
            We Track
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ duration: 1.05, ease, delay: 1.1 }}
            className="mt-2 md:mt-3 font-display font-black uppercase tracking-tight leading-[1.04] text-white"
            style={{
              fontSize: "clamp(1.85rem, 5.6vw, 4.5rem)",
              textShadow: "0 2px 22px rgba(0,0,0,0.7)",
            }}
            data-testid="infra-line-2b"
          >
            <span
              className="inline-block interactive-text interactive-text--gold"
              style={{
                color: "#FFB845",
                textShadow:
                  "0 0 24px rgba(255,153,51,0.7), 0 0 56px rgba(255,200,80,0.4)",
              }}
            >
              Bharat&apos;s
            </span>{" "}
            Rise.
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InfraPower;
